/**
 * Screen-scoped tokens for the **Manage seats** panel on Settings → My Plan.
 *
 * The panel is the seat twin of the SMS Credits buy panel: the same expanding
 * shell inside the card, the same heading step, the same order bar committing
 * the change. Its chrome, type steps and spacing therefore come from
 * `MyPlan.tokens`, and the geometry it shares with that panel is restated here
 * rather than imported from it — per-screen token files stay separate until a
 * promotion pass merges them.
 *
 * The stepper keeps the geometry the manage-seats dialog and "Choose your
 * plan" already use, so a seat count is dialled the same way everywhere.
 *
 * **No new colour is introduced:** every value resolves to a token that
 * already exists in `theme/colors.ts`.
 */

import myPlanTokens from "screens/Settings/MyPlan/MyPlan.tokens";
import { notification } from "theme/colors";

const t = myPlanTokens;

export const manageSeatsPanelTokens = {
  /** The expanding shell, matching the SMS credits buy panel. */
  panel: {
    paddingTop: "22px",
    paddingBottom: "24px",
    gap: "16px",
    bg: t.color.headerBg,
    headingFontSize: "13px",
    headingFontWeight: 800,
  },

  /** "You have · In use · Plan limit" — three figures, three labels. */
  stat: {
    gap: "8px",
    paddingX: "8px",
    paddingY: "10px",
    labelFontSize: "10px",
    labelLetterSpacing: "0.06em",
    valueFontSize: "17px",
    bg: t.color.surface,
    border: t.color.headerBorder,
  },

  /** The outlined [ − | value | + ] control. */
  stepper: {
    gap: "12px",
    labelFontSize: "12.5px",
    buttonSize: "38px",
    valueWidth: "64px",
    controlFontSize: "18px",
    valueFontSize: "20px",
    divider: t.color.headerBorder,
    hoverBg: t.color.hoverFill,
    /** Lavender grey, the same step the disabled order button fills with. */
    disabled: t.color.disabledControl,
    hintFontSize: "11.5px",
  },

  /** Itemised cost lines above the order bar. */
  cost: {
    rowGap: "6px",
    labelFontSize: "12.5px",
    valueFontSize: "13px",
    noteFontSize: "11.5px",
    /**
     * Money coming back to the customer. `notification.success.title` rather
     * than the brighter success icon green: at 13px this is normal-size text
     * needing 4.5:1, which the icon shade doesn't reach on white.
     */
    credit: notification.success.title,
  },

  /** Order summary bar, and the button that commits the change. */
  order: {
    padding: "14px 20px",
    radius: "12px",
    gap: "14px",
    lineFontSize: "12.5px",
    noteFontSize: "11.5px",
    totalFontSize: "20px",
    buttonHeight: "40px",
    buttonPaddingX: "20px",
    disabledBg: t.color.disabledControl,
  },
} as const;

export default manageSeatsPanelTokens;
